// Folder tree over flat paths: a file's name is its path in the workspace ("Tender/Lampiran/A12.pdf").
// Pure helpers, shared by the workspace page and the call's Files panel.

export const baseName = (path: string) => path.slice(path.lastIndexOf('/') + 1);
export const dirName = (path: string) => (path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '');
export const joinPath = (folder: string, name: string) => (folder ? `${folder}/${name}` : name);

/** Root first, then each folder on the way: [{label:'Workspace', path:''}, {label:'A', path:'A'}, …]. */
export function crumbs(folder: string, rootLabel: string): { label: string; path: string }[] {
  const out = [{ label: rootLabel, path: '' }];
  if (!folder) return out;
  const parts = folder.split('/');
  parts.forEach((p, i) => out.push({ label: p, path: parts.slice(0, i + 1).join('/') }));
  return out;
}

const byName = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });

/** What sits directly in `folder`: sub-folders (full paths) and files, each sorted by name. */
export function contents<F extends { name: string }>(files: F[], folders: string[], folder: string): { folders: string[]; files: F[] } {
  return {
    folders: folders.filter((p) => dirName(p) === folder && p !== folder).sort((a, b) => byName(baseName(a), baseName(b))),
    files: files.filter((f) => dirName(f.name) === folder).sort((a, b) => byName(baseName(a.name), baseName(b.name))),
  };
}

/** Number of files anywhere under a folder. */
export const countUnder = (files: { name: string }[], folder: string) => files.filter((f) => f.name.startsWith(`${folder}/`)).length;

/** Files and folders whose name contains every word of the query, anywhere in the tree. */
export function search<F extends { name: string }>(files: F[], folders: string[], query: string): { folders: string[]; files: F[] } {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return { folders: [], files: [] };
  const hit = (path: string) => words.every((w) => path.toLowerCase().includes(w));
  return { folders: folders.filter(hit), files: files.filter((f) => hit(f.name)) };
}

/** The path an item gets when dropped into `target`; null when the move is a no-op or impossible. */
export function moveTarget(kind: 'file' | 'folder', path: string, target: string): string | null {
  if (dirName(path) === target) return null;
  if (kind === 'folder' && (target === path || target.startsWith(`${path}/`))) return null;
  return joinPath(target, baseName(path));
}
